import { useRef } from 'react';
import { featureUtil } from 'utils/geoJSONFeatureUtils/featureUtil';
import { v4 as uuidv4 } from 'uuid';

import { GEOJSON_PROPERTY } from 'components/Map/hooks/useFeatures/constants';
import useMapContext from 'components/Map/hooks/useMapContext';

import { MAP_LAYER } from './constants';
import type { Feature, FlightResponse, LineString, Position } from './types';

const useFlightPath = () => {
    const { features } = useMapContext();
    const { addFeature, removeFeature } = features;
    const { createLinePolygonWithWidthMeters, createLineStringFeature } = featureUtil;
    const highlightFeatureIdsRef = useRef<string[]>([]);

    const initializeFlightPaths = (flightPaths?: FlightResponse[]) => {
        if (!flightPaths || flightPaths.length === 0) {
            return;
        }

        flightPaths?.forEach(initializeFlightPath);
    };

    const initializeFlightPath = ({
        flightPath,
        id: flightId,
        sprayPath,
        swathWidthMeters
    }: FlightResponse) => {
        const flightPathFeature = {
            geometry: {
                coordinates: flightPath.coordinates as Position[],
                type: 'LineString'
            },
            id: uuidv4(),
            properties: {
                [GEOJSON_PROPERTY.FLIGHT_ID]: flightId,
                [GEOJSON_PROPERTY.IS_FULL_FLIGHT_PATH]: true
            },
            type: 'Feature'
        } as Feature<LineString>;

        addFeature({
            feature: flightPathFeature,
            layerName: MAP_LAYER.FLIGHT_PATH_LINE
        });

        for (const line of sprayPath.coordinates as Position[][]) {
            const lineFeature = createLineStringFeature(line, uuidv4());

            lineFeature.properties = {
                ...lineFeature.properties,
                [GEOJSON_PROPERTY.FLIGHT_ID]: flightId
            };

            addFeature({
                feature: lineFeature,
                layerName: MAP_LAYER.FLIGHT_PATH_LINE
            });

            const linePolygonWithWidthMeters = createLinePolygonWithWidthMeters(
                {
                    geometry: {
                        coordinates: [...lineFeature.geometry.coordinates],
                        type: 'LineString'
                    },
                    id: uuidv4(),
                    properties: {
                        [GEOJSON_PROPERTY.FLIGHT_ID]: flightId
                    },
                    type: 'Feature'
                } as Feature<LineString>,
                swathWidthMeters / 2
            );

            addFeature({
                feature: linePolygonWithWidthMeters,
                layerName: MAP_LAYER.FLIGHT_PATH_FILL
            });
        }
    };

    const clearHighlight = () => {
        for (const featureId of highlightFeatureIdsRef.current) {
            removeFeature(featureId);
        }

        highlightFeatureIdsRef.current = [];
    };

    const highlightFlight = (flight?: FlightResponse) => {
        clearHighlight();

        if (!flight) {
            return;
        }

        const { flightPath, id: flightId, sprayPath, swathWidthMeters } = flight;
        const featureIds: string[] = [];

        const flightPathFeature = {
            geometry: {
                coordinates: flightPath.coordinates as Position[],
                type: 'LineString'
            },
            id: uuidv4(),
            properties: {
                [GEOJSON_PROPERTY.FLIGHT_ID]: flightId,
                [GEOJSON_PROPERTY.IS_FULL_FLIGHT_PATH]: true
            },
            type: 'Feature'
        } as Feature<LineString>;

        const addedLine = addFeature({
            feature: flightPathFeature,
            layerName: MAP_LAYER.FLIGHT_PATH_HIGHLIGHT_LINE
        });

        featureIds.push(addedLine.properties?.[GEOJSON_PROPERTY.ID] as string);

        for (const line of sprayPath.coordinates as Position[][]) {
            const lineFeature = createLineStringFeature(line, uuidv4());

            lineFeature.properties = {
                ...lineFeature.properties,
                [GEOJSON_PROPERTY.FLIGHT_ID]: flightId
            };

            const addedSprayLine = addFeature({
                feature: lineFeature,
                layerName: MAP_LAYER.FLIGHT_PATH_HIGHLIGHT_LINE
            });

            featureIds.push(addedSprayLine.properties?.[GEOJSON_PROPERTY.ID] as string);

            const linePolygonWithWidthMeters = createLinePolygonWithWidthMeters(
                {
                    geometry: {
                        coordinates: [...lineFeature.geometry.coordinates],
                        type: 'LineString'
                    },
                    id: uuidv4(),
                    properties: {
                        [GEOJSON_PROPERTY.FLIGHT_ID]: flightId
                    },
                    type: 'Feature'
                } as Feature<LineString>,
                swathWidthMeters / 2
            );

            const addedFill = addFeature({
                feature: linePolygonWithWidthMeters,
                layerName: MAP_LAYER.FLIGHT_PATH_HIGHLIGHT_FILL
            });

            featureIds.push(addedFill.properties?.[GEOJSON_PROPERTY.ID] as string);
        }

        highlightFeatureIdsRef.current = featureIds;
    };

    return {
        clearHighlight,
        highlightFlight,
        initializeFlightPaths
    };
};

export default useFlightPath;
